import React, { useState } from 'react';
import { ETHNICITIES, LANGUAGES, SKILLS, CORE_SKILLS } from './filterEnums';
import {
    Modal,
    Typography,
    Select,
    MenuItem,
    Box,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Slider,
    FormControl,
    IconButton,
    Stack,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import CustomButton from '../Button/CustomButton';
import { makeStyles } from '@mui/styles';
import LocationAutocomplete from '../LocationAutocomplete/LocationAutocomplete';
import AutocompleteMultiSelect from '../AutocompleteMultiSelect/AutocompleteMultiSelect';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { TProfileType } from '../../types/global/helper';

interface TalentFiltersMobileProps {
    open: boolean;
    onClose: () => void;
    filters: any;
    onChange: (filters: any) => void;
    onResetFilter?: () => void;
    profileType?: TProfileType;
}

const genderOptions = ['male', 'female', 'others'];
const hairTypeOptions = ['straight', 'wavy', 'curly', 'any'];
const bodyTypeOptions = ['athletic', 'average', 'curvy', 'any'];

const useStyles = makeStyles(theme => ({
    root: {
        borderRadius: 12,
        padding: 24,
        marginBottom: 24,
        minWidth: 320,
        width: '100%',
    },
    filterChip: {
        margin: '0 8px 8px 0',
    },
    filterSection: {
        marginBottom: 16,
    },
    filterRow: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 24,
        marginBottom: 16,
    },
    filterField: {
        minWidth: 120,
        fontFamily: FONT_ENUM.MAIN_MEDIUM,
    },
    buttonGroup: {
        display: 'flex',
        gap: 16,
        justifyContent: 'flex-end',
        marginTop: 16,
    },
    label: {
        fontFamily: FONT_ENUM.MAIN_MEDIUM,
        fontWeight: 600,
        fontSize: 14,
        marginBottom: 4,
    },
}));

const TalentFiltersMobile: React.FC<TalentFiltersMobileProps> = ({
    open,
    onClose,
    filters,
    onChange,
    onResetFilter,
    profileType = 'performer',
}) => {
    const classes = useStyles();
    const { themeStyle } = useThemeContext();
    const [local, setLocal] = useState(filters || {});
    const [locationValue, setLocationValue] = useState<any>(null);

    // Get skills based on profile type
    const availableSkills = CORE_SKILLS[profileType] || SKILLS;

    // Age slider
    const handleAgeChange = (_: any, newValue: number | number[]) => {
        const [min, max] = newValue as number[];
        setLocal({ ...local, ageFrom: min, ageTo: max });
    };

    // Rating slider
    const handleRatingChange = (_: any, value: number | number[]) => {
        setLocal({ ...local, rating: value });
    };

    // Gender
    const handleGenderChange = (e: any) => {
        setLocal({ ...local, gender: e.target.value });
    };

    // Skills/Languages - now handled directly in AutocompleteMultiSelect components

    // Appearance
    const handleEthnicityChange = (e: any) => {
        setLocal({ ...local, ethnicity: e.target.value });
    };
    // const handleHairColorChange = (e: any) => {
    //     setLocal({ ...local, hair_color: e.target.value });
    // };
    const handleHairTypeChange = (e: any) => {
        setLocal({ ...local, hair_type: e.target.value });
    };
    const handleBodyTypeChange = (e: any) => {
        setLocal({ ...local, body_type: e.target.value });
    };

    // Location (LocationAutocomplete)
    const handleLocationChange = (address: string, coordinates?: number[]) => {
        if (coordinates) {
            setLocationValue({ address });
            setLocal({ ...local, location: address, lat: coordinates[0], lng: coordinates[1] });
        }
    };

    // Apply filters
    const handleApply = () => {
        onChange(local);
        onClose();
    };

    const handleReset = () => {
        setLocal({});
        setLocationValue(null);
        // Clear all filters except profile_type, and clear search
        onChange({ profile_type: filters.profile_type });
        onResetFilter && onResetFilter();
        onClose();
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backdropFilter: 'blur(8px)',
            }}>
            <Box
                sx={{
                    width: '100vw',
                    height: '100dvh',
                    bgcolor: theme[themeStyle].secondaryBg,
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    overflow: 'auto',
                }}>
                {/* Header */}
                <Box
                    sx={{
                        width: '100%',
                        minHeight: 60,
                        bgcolor: theme[themeStyle].deep_grey,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 2,
                        py: 1,
                        position: 'sticky',
                        top: 0,
                        zIndex: 1,
                    }}>
                    <Typography
                        sx={{
                            color: theme[themeStyle].tag_white,
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontWeight: 600,
                            fontSize: 18,
                        }}>
                        Filters
                    </Typography>
                    <IconButton
                        onClick={onClose}
                        sx={{
                            color: theme[themeStyle].tag_white,
                            '&:hover': {
                                bgcolor: theme[themeStyle].highlight + '33',
                            },
                        }}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                {/* Content */}
                <Box sx={{ flex: 1, p: 2, overflow: 'auto' }}>
                    {/* Basic Filters */}
                    <Accordion
                        defaultExpanded
                        sx={{
                            bgcolor: theme[themeStyle].secondaryBg,
                            border: `1px solid ${theme[themeStyle].grey}`,
                            mb: 2,
                            '&:before': {
                                display: 'none',
                            },
                        }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: theme[themeStyle].tag_white }} />}>
                            <Typography
                                sx={{
                                    color: theme[themeStyle].tag_white,
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    fontWeight: 600,
                                    fontSize: 14,
                                }}>
                                Basic Filters
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ bgcolor: theme[themeStyle].secondaryBg }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Box>
                                    <Typography
                                        sx={{
                                            color: theme[themeStyle].tag_white,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            mb: 1,
                                        }}>
                                        Age Range
                                    </Typography>
                                    <Slider
                                        value={[local.ageFrom || 18, local.ageTo || 60]}
                                        min={10}
                                        max={100}
                                        step={1}
                                        onChange={handleAgeChange}
                                        valueLabelDisplay="auto"
                                        sx={{
                                            width: '100%',
                                            color: theme[themeStyle].highlight,
                                            '& .MuiSlider-thumb': { bgcolor: theme[themeStyle].highlight },
                                            '& .MuiSlider-track': { bgcolor: theme[themeStyle].highlight },
                                            '& .MuiSlider-rail': { bgcolor: theme[themeStyle].tag_white, opacity: 0.2 },
                                        }}
                                    />
                                </Box>
                                <Box>
                                    <Typography
                                        sx={{
                                            color: theme[themeStyle].tag_white,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            mb: 1,
                                        }}>
                                        Gender
                                    </Typography>
                                    <FormControl fullWidth>
                                        <Select
                                            value={local.gender || ''}
                                            onChange={handleGenderChange}
                                            displayEmpty
                                            className={classes.filterField}
                                            size="small"
                                            sx={{
                                                bgcolor: theme[themeStyle].secondaryBg,
                                                color: theme[themeStyle].tag_white,
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                '& .MuiSelect-icon': { color: theme[themeStyle].tag_white },
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: theme[themeStyle].tag_white,
                                                },
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: theme[themeStyle].tag_white,
                                                },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: theme[themeStyle].tag_white,
                                                },
                                            }}
                                            MenuProps={{
                                                PaperProps: {
                                                    sx: {
                                                        bgcolor: theme[themeStyle].secondaryBg,
                                                        color: theme[themeStyle].tag_white,
                                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                    },
                                                },
                                            }}>
                                            <MenuItem value="">Any Gender</MenuItem>
                                            {genderOptions.map(g => (
                                                <MenuItem
                                                    key={g}
                                                    value={g}
                                                    style={{ fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                                                    {g}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>
                                <Box>
                                    <Typography
                                        sx={{
                                            color: theme[themeStyle].tag_white,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            mb: 1,
                                        }}>
                                        Rating
                                    </Typography>
                                    <Slider
                                        value={local.rating || 0}
                                        min={0}
                                        max={5}
                                        step={0.1}
                                        onChange={handleRatingChange}
                                        valueLabelDisplay="auto"
                                        sx={{
                                            width: '100%',
                                            color: theme[themeStyle].highlight,
                                            '& .MuiSlider-thumb': { bgcolor: theme[themeStyle].highlight },
                                            '& .MuiSlider-track': { bgcolor: theme[themeStyle].highlight },
                                            '& .MuiSlider-rail': { bgcolor: theme[themeStyle].tag_white, opacity: 0.2 },
                                        }}
                                    />
                                </Box>
                            </Box>
                        </AccordionDetails>
                    </Accordion>

                    {/* Location Filter */}
                    <Accordion
                        sx={{
                            bgcolor: theme[themeStyle].secondaryBg,
                            border: `1px solid ${theme[themeStyle].grey}`,
                            mb: 2,
                            '&:before': {
                                display: 'none',
                            },
                        }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: theme[themeStyle].tag_white }} />}>
                            <Typography
                                sx={{
                                    color: theme[themeStyle].tag_white,
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    fontWeight: 600,
                                    fontSize: 14,
                                }}>
                                Search & Select Location
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ bgcolor: theme[themeStyle].secondaryBg }}>
                            <LocationAutocomplete
                                value={locationValue}
                                onSelect={handleLocationChange}
                                withCordinates
                                onSelectLocationOption={() => {}}
                                searchOptions={{ types: ['(regions)'] }}
                                placeholder="Search state or country..."
                                fieldStyle={
                                    {
                                        bgcolor: theme[themeStyle].secondaryBg,
                                        borderRadius: '8px',
                                        input: { color: theme[themeStyle].tag_white },
                                        marginTop: '0px',
                                        label: { color: theme[themeStyle].tag_white },
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme[themeStyle].tag_white,
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme[themeStyle].tag_white,
                                        },
                                        '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme[themeStyle].tag_white,
                                        },
                                    } as any
                                }
                            />
                        </AccordionDetails>
                    </Accordion>

                    {/* Skills & Languages */}
                    <Accordion
                        sx={{
                            bgcolor: theme[themeStyle].secondaryBg,
                            border: `1px solid ${theme[themeStyle].grey}`,
                            mb: 2,
                            '&:before': {
                                display: 'none',
                            },
                        }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: theme[themeStyle].tag_white }} />}>
                            <Typography
                                sx={{
                                    color: theme[themeStyle].tag_white,
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    fontWeight: 600,
                                    fontSize: 14,
                                }}>
                                Skills & Languages
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ bgcolor: theme[themeStyle].secondaryBg }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Box>
                                    <AutocompleteMultiSelect
                                        options={[...availableSkills]}
                                        value={local.skills || []}
                                        onChange={value => setLocal({ ...local, skills: value })}
                                        placeholder="Search and select skills..."
                                        label="Skills"
                                        size="small"
                                    />
                                </Box>
                                <Box>
                                    <AutocompleteMultiSelect
                                        options={[...LANGUAGES]}
                                        value={local.languages || []}
                                        onChange={value => setLocal({ ...local, languages: value })}
                                        placeholder="Search and select languages..."
                                        label="Languages"
                                        size="small"
                                    />
                                </Box>
                            </Box>
                        </AccordionDetails>
                    </Accordion>

                    {/* Appearance */}
                    <Accordion
                        sx={{
                            bgcolor: theme[themeStyle].secondaryBg,
                            border: `1px solid ${theme[themeStyle].grey}`,
                            mb: 2,
                            '&:before': {
                                display: 'none',
                            },
                        }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: theme[themeStyle].tag_white }} />}>
                            <Typography
                                sx={{
                                    color: theme[themeStyle].tag_white,
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    fontWeight: 600,
                                    fontSize: 14,
                                }}>
                                Appearance
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ bgcolor: theme[themeStyle].secondaryBg }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Box>
                                    <Typography
                                        sx={{
                                            color: theme[themeStyle].tag_white,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            mb: 1,
                                        }}>
                                        Ethnicity
                                    </Typography>
                                    <FormControl fullWidth>
                                        <Select
                                            value={local.ethnicity || ''}
                                            onChange={handleEthnicityChange}
                                            displayEmpty
                                            size="small"
                                            sx={{
                                                bgcolor: theme[themeStyle].secondaryBg,
                                                color: theme[themeStyle].tag_white,
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                '& .MuiSelect-icon': { color: theme[themeStyle].tag_white },
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: theme[themeStyle].tag_white,
                                                },
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: theme[themeStyle].tag_white,
                                                },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: theme[themeStyle].tag_white,
                                                },
                                            }}
                                            MenuProps={{
                                                PaperProps: {
                                                    sx: {
                                                        bgcolor: theme[themeStyle].secondaryBg,
                                                        color: theme[themeStyle].tag_white,
                                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                    },
                                                },
                                            }}>
                                            <MenuItem value="">Any Ethnicity</MenuItem>
                                            {ETHNICITIES.map(ethnicity => (
                                                <MenuItem
                                                    key={ethnicity}
                                                    value={ethnicity}
                                                    style={{ fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                                                    {ethnicity}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>
                                <Box>
                                    <Typography
                                        sx={{
                                            color: theme[themeStyle].tag_white,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            mb: 1,
                                        }}>
                                        Hair Type
                                    </Typography>
                                    <FormControl fullWidth>
                                        <Select
                                            value={local.hair_type || ''}
                                            onChange={handleHairTypeChange}
                                            displayEmpty
                                            size="small"
                                            sx={{
                                                bgcolor: theme[themeStyle].secondaryBg,
                                                color: theme[themeStyle].tag_white,
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                '& .MuiSelect-icon': { color: theme[themeStyle].tag_white },
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: theme[themeStyle].tag_white,
                                                },
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: theme[themeStyle].tag_white,
                                                },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: theme[themeStyle].tag_white,
                                                },
                                            }}
                                            MenuProps={{
                                                PaperProps: {
                                                    sx: {
                                                        bgcolor: theme[themeStyle].secondaryBg,
                                                        color: theme[themeStyle].tag_white,
                                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                    },
                                                },
                                            }}>
                                            <MenuItem value="">Any Hair Type</MenuItem>
                                            {hairTypeOptions.map(type => (
                                                <MenuItem
                                                    key={type}
                                                    value={type}
                                                    style={{ fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                                                    {type}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>
                                <Box>
                                    <Typography
                                        sx={{
                                            color: theme[themeStyle].tag_white,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            mb: 1,
                                        }}>
                                        Body Type
                                    </Typography>
                                    <FormControl fullWidth>
                                        <Select
                                            value={local.body_type || ''}
                                            onChange={handleBodyTypeChange}
                                            displayEmpty
                                            size="small"
                                            sx={{
                                                bgcolor: theme[themeStyle].secondaryBg,
                                                color: theme[themeStyle].tag_white,
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                '& .MuiSelect-icon': { color: theme[themeStyle].tag_white },
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: theme[themeStyle].tag_white,
                                                },
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: theme[themeStyle].tag_white,
                                                },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: theme[themeStyle].tag_white,
                                                },
                                            }}
                                            MenuProps={{
                                                PaperProps: {
                                                    sx: {
                                                        bgcolor: theme[themeStyle].secondaryBg,
                                                        color: theme[themeStyle].tag_white,
                                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                    },
                                                },
                                            }}>
                                            <MenuItem value="">Any Body Type</MenuItem>
                                            {bodyTypeOptions.map(type => (
                                                <MenuItem
                                                    key={type}
                                                    value={type}
                                                    style={{ fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                                                    {type}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                </Box>

                {/* Footer with buttons */}
                <Box
                    sx={{
                        width: '100%',
                        p: 2,
                        bgcolor: theme[themeStyle].deep_grey,
                        borderTop: `1px solid ${theme[themeStyle].grey}`,
                        position: 'sticky',
                        bottom: 0,
                    }}>
                    <Stack direction="row" spacing={2} sx={{ justifyContent: 'space-between' }}>
                        <CustomButton
                            label="Reset"
                            onClick={handleReset}
                            customStyle={{
                                border: `1px solid ${theme[themeStyle].highlight}`,
                                color: theme[themeStyle].highlight,
                            }}
                        />
                        <CustomButton
                            label="Apply Filters"
                            onClick={handleApply}
                            customStyle={{
                                color: theme[themeStyle].black,
                            }}
                        />
                    </Stack>
                </Box>
            </Box>
        </Modal>
    );
};

export default TalentFiltersMobile;
